import React from "react";
import { ScraperJob } from "./types";
import { IconTrash } from "@velaro/velaro-component-lib";
import ScraperJobActivityLog from "./ScraperJobActivityLog";
import GapReportPanel from "./GapReportPanel";

interface ScraperJobRowProps {
  job: ScraperJob;
  onViewResults: () => void;
  onDelete: () => void;
  onResume: () => void;
  onStop?: () => void;
  onPublish?: () => void;
  onIndexToKb?: () => void;
  onExportToSharePoint?: () => void;
  onToggleActivity: () => void;
  isActivityOpen: boolean;
  isGapReportOpen: boolean;
  onToggleGapReport: () => void;
  scraperApiUrl: string;
  getHeaders: () => Record<string, string>;
  m365Connected?: boolean;
  isPublished?: boolean;
}

// Shared pill button base — all action buttons use this
const pillBase = "inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors border whitespace-nowrap";

const pillVariants = {
  slate:   `${pillBase} bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100`,
  emerald: `${pillBase} bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100`,
  blue:    `${pillBase} bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100`,
  purple:  `${pillBase} bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100`,
  green:   `${pillBase} bg-green-600 border-green-600 text-white hover:bg-green-700`,
  greenGhost: `${pillBase} bg-green-50 border-green-200 text-green-700 hover:bg-green-100`,
  orange:  `${pillBase} bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100`,
  red:     `${pillBase} bg-red-50 border-red-200 text-red-600 hover:bg-red-100`,
};

export default function ScraperJobRow({
  job,
  onViewResults,
  onDelete,
  onResume,
  onStop,
  onPublish,
  onIndexToKb,
  onExportToSharePoint,
  onToggleActivity,
  isActivityOpen,
  isGapReportOpen,
  onToggleGapReport,
  scraperApiUrl,
  getHeaders,
  m365Connected,
  isPublished,
}: ScraperJobRowProps) {
  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      pending: "bg-yellow-100 text-yellow-800",
      running: "bg-blue-100 text-blue-800",
      completed: "bg-green-100 text-green-800",
      failed: "bg-red-100 text-red-800"
    };
    return colors[status] || "bg-slate-100 text-slate-800";
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const progress = parseInt(job.progress) || 0;

  return (
    <>
      <tr className="border-b hover:bg-slate-50">
        <td className="p-3">
          <div className="font-medium">{job.name}</div>
          <div className="text-xs text-slate-400">{job.job_id}</div>
        </td>
        <td className="p-3">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(job.status)}`}>
            {job.status}
          </span>
        </td>
        <td className="p-3">
          {job.status === "running" || job.status === "pending" ? (
            <div className="w-32">
              <div className="flex justify-between text-xs text-slate-500 mb-1">
                <span>{progress}%</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          ) : (
            <span className="text-sm text-slate-500">
              {job.status === "completed" ? "100%" : "-"}
            </span>
          )}
        </td>
        <td className="p-3">
          <div className="flex flex-col gap-0.5">
            <span className="text-sm">{job.pages_scraped} indexed</span>
            {(job.pages_blocked ?? 0) > 0 && (
              <span className="text-xs text-red-500">{job.pages_blocked} blocked</span>
            )}
            {(job.pages_thin ?? 0) > 0 && (
              <span className="text-xs text-amber-500">{job.pages_thin} thin</span>
            )}
          </div>
        </td>
        <td className="p-3">
          <span className="text-sm text-slate-500">
            {formatDate(job.created_at)}
          </span>
        </td>
        <td className="p-3 text-right">
          <div className="flex justify-end flex-wrap gap-1.5">
            {/* Activity toggle */}
            <button
              onClick={onToggleActivity}
              className={isActivityOpen ? pillVariants.slate + " ring-1 ring-slate-300" : pillVariants.slate}
            >
              {isActivityOpen ? "▲ Hide" : "▼ Activity"}
            </button>

            {job.status === "completed" && (
              <>
                {/* Coverage report */}
                <button
                  onClick={onToggleGapReport}
                  className={isGapReportOpen ? pillVariants.emerald + " ring-1 ring-emerald-300" : pillVariants.emerald}
                  title="See what pages were indexed, blocked, or missed"
                >
                  {isGapReportOpen ? "▲ Coverage" : "📊 Coverage"}
                </button>

                {/* View results */}
                <button
                  onClick={onViewResults}
                  className={pillVariants.blue}
                >
                  📄 Results
                </button>

                {/* Publish — shows Published badge when done */}
                {onPublish && (
                  isPublished ? (
                    <span className={pillVariants.greenGhost}>
                      ✓ Published
                    </span>
                  ) : (
                    <button
                      onClick={onPublish}
                      className={pillVariants.green}
                    >
                      ↑ Publish
                    </button>
                  )
                )}

                {/* SharePoint export */}
                {m365Connected && onExportToSharePoint && (
                  <button
                    onClick={onExportToSharePoint}
                    className={pillVariants.slate}
                    title="Export results to SharePoint"
                  >
                    ☁️ SharePoint
                  </button>
                )}
              </>
            )}

            {/* Stop running job */}
            {(job.status === "running" || job.status === "pending") && onStop && (
              <button
                onClick={onStop}
                className={pillVariants.orange}
                title="Stop this job"
              >
                ■ Stop
              </button>
            )}

            {/* Failed jobs — let users see partial results + the activity log */}
            {job.status === "failed" && (
              <>
                {job.pages_scraped > 0 && (
                  <button
                    onClick={onViewResults}
                    className={pillVariants.blue}
                    title="View pages that were scraped before the job failed"
                  >
                    📄 Results
                  </button>
                )}
                {job.can_resume && (
                  <button
                    onClick={onResume}
                    className={pillVariants.green}
                  >
                    ▶ Resume
                  </button>
                )}
              </>
            )}

            {/* Delete */}
            {job.status !== "running" && job.status !== "pending" && (
              <button
                onClick={onDelete}
                className={pillVariants.red}
                title="Delete job"
              >
                <IconTrash className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

        </td>
      </tr>

      {/* Full-width error banner for failed jobs — always visible, no truncation,
          with a hint to open Activity for full log context */}
      {job.status === "failed" && job.error && (
        <tr className="bg-red-50 border-b border-red-200">
          <td colSpan={6} className="p-3">
            <div className="flex items-start gap-2 text-xs text-red-800">
              <span className="font-semibold flex-shrink-0">Job failed:</span>
              <span className="whitespace-pre-wrap break-words flex-1">{job.error}</span>
              {!isActivityOpen && (
                <button
                  onClick={onToggleActivity}
                  className="flex-shrink-0 underline text-red-700 hover:text-red-900"
                >
                  View full activity log →
                </button>
              )}
            </div>
          </td>
        </tr>
      )}

      {isActivityOpen && (
        <ScraperJobActivityLog
          jobId={job.job_id}
          jobStatus={job.status}
          scraperApiUrl={scraperApiUrl}
          getHeaders={getHeaders}
        />
      )}

      {isGapReportOpen && (
        <GapReportPanel
          jobId={job.job_id}
          jobStatus={job.status}
          scraperApiUrl={scraperApiUrl}
          getHeaders={getHeaders}
        />
      )}
    </>
  );
}
